import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUIGeneralDrawerHeader } from '../../../../../../../atomic/molecules';
import { Grid, TextField } from '@mui/material';
import { SCUIButton, SCUILoadingLinear } from '../../../../../../../atomic/atoms';
import Swal from 'sweetalert2';
import { CustomerCommand, OrderCommand } from '../../../../../../../services/command';
import { CustomerAddressEntityFieldsV2 } from '../../../../../../CustomerManagement/components/CustomerAdressInformationPaper/CustomerAddressEntityFieldsV2';
import { DeliveryMethodEnum } from '../../../../../../../types/enums';
import { AIC_US, aicCls } from '../addressInformationCard.classes';
import {
    ADDRESS_TYPE_SHIPPING,
    buildUpdateCustomerAddressInit,
    buildUpdateCustomerAddressPayload,
} from './updateCustomerAddressPayload.helper';

function UpdateSaleOrderAddress({ detail, onClose, setRefresh, addressRules, deliveryMethodId }: any) {
    const { t } = useTranslation();
    const [saleOrderAddress, setSaleOrderAddress] = useState<any>(() => buildUpdateCustomerAddressInit(detail));
    const [loading, setLoading] = useState(false);
    const isAddressFieldDisabled =
        Number(deliveryMethodId) === DeliveryMethodEnum.StorePickup ||
        Number(deliveryMethodId) === DeliveryMethodEnum.PickPoint;
    const UpdateCustomerAddress = async () => {
        setLoading(true);
        if (
            !saleOrderAddress.phoneNumber ||
            saleOrderAddress.phoneNumber.length < 7 ||
            saleOrderAddress.phoneNumber.length > 15
        ) {
            setLoading(false);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('addressDetail.missingPhoneNumber'),
            });
            return;
        }

        try {
            const payload = buildUpdateCustomerAddressPayload(
                saleOrderAddress,
                deliveryMethodId,
                ADDRESS_TYPE_SHIPPING
            );
            const customerRes: any = await CustomerCommand.UpdateCustomerAddress(payload);
            if (!customerRes) {
                Swal.fire('', ' ', 'error');
                return;
            }
            if (customerRes.statusCode !== 200 && customerRes?.error !== null && customerRes.error?.length > 0) {
                Swal.fire(customerRes?.error, ' ', 'error');
                return;
            }
            if (customerRes?.model?.operationStatus === false) {
                Swal.fire(customerRes?.model?.errorMessageKeyword || '', ' ', 'error');
                return;
            }

            const customerAddressId =
                customerRes?.model?.customerAddressId ?? customerRes?.data?.customerAddressId;
            if (!customerAddressId) {
                Swal.fire('', ' ', 'error');
                return;
            }

            const req = {
                customerId: detail.customerId,
                orderId: detail.orderId,
                orderNumber: detail.orderNumber,
                billingAddressId: detail.billingAddressId,
                shippingAddressId: customerAddressId,
                confirmationCode: null,
                otpControlAvailable: false,
            };
            const orderRes: any = await OrderCommand.UpdateSaleOrderAddress(req);
            if (orderRes) {
                if (orderRes.error && orderRes.error != null && orderRes.error !== 'null') {
                    Swal.fire('', ' ', 'error');
                } else {
                    Swal.fire(t('general.success'), t('general.updated'), 'success');
                    onClose();
                    setRefresh((prevLoading: any) => !prevLoading);
                }
            }
        } catch (error: any) {
            Swal.fire('', error?.toString?.() || ' ', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            className={AIC_US.form}
            onSubmit={(e: any) => {
                e.preventDefault();
            }}
        >
            <div className={aicCls('commentFilterContainer', AIC_US.root)}>
                <Grid className={AIC_US.grid} container spacing={1}>
                    <SCUIGeneralDrawerHeader
                        header={t('orderDetail.addressEdit')}
                        onClose={onClose}
                        rootClassName={AIC_US.drawerHeaderRoot}
                        titleClassName={AIC_US.drawerHeaderTitle}
                        closeIconClassName={AIC_US.drawerHeaderClose}
                    />
                    <div className={AIC_US.loadingWrap}>
                        <SCUILoadingLinear display={loading} />
                    </div>
                    <p className={aicCls('subheadtxt', AIC_US.addressSubtitle)}>
                        {t('orderDetail.addressNo')}: {detail?.customerAddressId}
                    </p>
                    <Grid
                        className={AIC_US.rowFirstName}
                        sx={{ marginTop: 0.5 }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <TextField
                            className={AIC_US.inputTextFirstName}
                            defaultValue={saleOrderAddress?.firstName}
                            onChange={(e) =>
                                setSaleOrderAddress({
                                    ...saleOrderAddress,
                                    firstName: e.target.value.trim(),
                                })
                            }
                            label={t('general.name')}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid
                        className={AIC_US.rowLastName}
                        sx={{ marginTop: 0.5 }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <TextField
                            className={AIC_US.inputTextLastName}
                            defaultValue={saleOrderAddress?.lastName}
                            onChange={(e) =>
                                setSaleOrderAddress({
                                    ...saleOrderAddress,
                                    lastName: e.target.value.trim(),
                                })
                            }
                            label={t('general.surname')}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }} className={AIC_US.addressEntityWrap}>
                        <CustomerAddressEntityFieldsV2
                            customerAddress={saleOrderAddress}
                            setCustomerAddress={setSaleOrderAddress}
                            addressRules={addressRules}
                            deliveryMethodId={deliveryMethodId}
                        />
                    </Grid>
                    <Grid
                        className={AIC_US.rowPhone}
                        sx={{ marginTop: 0.5 }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <TextField
                            className={AIC_US.inputTextPhone}
                            defaultValue={saleOrderAddress?.phoneNumber?.trim?.() ?? ''}
                            label={t('general.phone')}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            onChange={(e) =>
                                setSaleOrderAddress({
                                    ...saleOrderAddress,
                                    phoneNumber: e.target.value.trim(),
                                })
                            }
                            fullWidth
                            type="phone"
                        />
                    </Grid>
                    <Grid
                        className={AIC_US.rowAddress}
                        sx={{ marginTop: 0.5 }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <TextField
                            className={AIC_US.inputTextAddress}
                            value={saleOrderAddress?.address ?? ''}
                            multiline
                            rows={5}
                            label={t('general.address')}
                            disabled={isAddressFieldDisabled}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            onChange={(e) =>
                                setSaleOrderAddress({
                                    ...saleOrderAddress,
                                    address: e.target.value.trim(),
                                })
                            }
                            fullWidth
                        />
                    </Grid>
                    <Grid
                        className={AIC_US.rowSubmit}
                        sx={{ marginTop: 0.5 }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <SCUIButton
                            className={AIC_US.inputButtonUpdate}
                            text={t('general.update')}
                            type="primary"
                            sizeType="fixed"
                            buttonType="submit"
                            size={'m'}
                            fullwidth
                            onClick={UpdateCustomerAddress}
                        />
                    </Grid>
                </Grid>
            </div>
        </form>
    );
}

export default UpdateSaleOrderAddress;
